import React, { Fragment, useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { connect } from 'react-redux';

import styles from './QQLoginPieChart.module.css';
import QQLoginPieChartTotal from '../QQLoginPieChartTotal/QQLoginPieChartTotal';
import { getConfigObject } from '../../../../../util/config';
import { LOGIN_SLICE_NAMES } from '../../../../constants';
import {
  getLoginPieA11yLabel,
  preventDescendantFocus,
} from '../../../../utils';

const COLORS = ['#DA0052', '#C8C8C8'];
const MIN_GRAPHIC_WIDTH = 420;
const CARD_INFO_HEIGHT = 260;
const PADDING_ANGLE = 2;

const legendWrapperStyle = {
  top: 0,
  left: '50%',
  display: 'flex',
  alignItems: 'center',
  height: '100%',
};

const CustomTooltip = ({ payload }) => {
  const payloadData = get(payload, '[0]');
  if (payloadData) {
    const { name, value } = payloadData;
    const loggedOrNot =
      name === LOGIN_SLICE_NAMES.NOT_LOGGED_IN ? 'have not' : 'have';
    const legend = `${value} students ${loggedOrNot} logged in`;
    return (
      <div className={styles.customTooltip}>
        <div className={styles.customTooltipLegend}>{legend}</div>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  payload: PropTypes.array,
};

const RenderLegend = ({ payload }) => (
  <div className={styles.chartWrapper}>
    {payload.map((entry, index) => {
      const {
        value: name,
        payload: { value },
      } = entry;
      let legend = (
        <div>
          <span className={styles.value}>{value}</span> students{' '}
          <span className={styles.emphasis}> have </span> logged in
        </div>
      );
      let legendSquareColor = <div className={styles.legendPointLoggedIn} />;
      if (name === LOGIN_SLICE_NAMES.NOT_LOGGED_IN) {
        legend = (
          <div>
            <span className={styles.value}>{value}</span> students{' '}
            <span className={styles.emphasis}> have not </span> logged in
          </div>
        );
        legendSquareColor = <div className={styles.legendPointNotLoggedIn} />;
      }
      return (
        <div className={styles.legend} key={`item-${index}`}>
          {legendSquareColor}
          {legend}
        </div>
      );
    })}
  </div>
);

RenderLegend.propTypes = {
  payload: PropTypes.array,
};

function QQLoginPieChart({ data, containerWidth }) {
  const { FF_LOGIN_INSIGHTS_TOTAL } = getConfigObject();

  if (FF_LOGIN_INSIGHTS_TOTAL)
    return <QQLoginPieChartTotal data={data} containerWidth={containerWidth} />;

  const [width, setWidth] = useState(0);
  const elementRef = useRef(null);

  const insightsData =
    data &&
    data.insightsData.filter((d) => d.name !== LOGIN_SLICE_NAMES.LOGIN_DATA);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    if (rect.width) setWidth(rect.width);
  }, [elementRef.current, containerWidth]);

  const SHOW_ONLY_LEGEND = width < MIN_GRAPHIC_WIDTH;

  const chartAltText = getLoginPieA11yLabel(data);

  return (
    <div
      ref={elementRef}
      className={
        SHOW_ONLY_LEGEND ? styles.onlyLegendContainer : styles.widgetContainer
      }
    >
      {!SHOW_ONLY_LEGEND && (
        <Fragment>
          <div
            role="img"
            aria-label={chartAltText}
            onMouseDown={preventDescendantFocus}
          >
            <ResponsiveContainer height={CARD_INFO_HEIGHT}>
              <PieChart aria-hidden="true">
                <Pie
                  rootTabIndex={-1}
                  data={insightsData || null}
                  cx="25%"
                  cy="50%"
                  innerRadius={'50%'}
                  outerRadius={'80%'}
                  paddingAngle={PADDING_ANGLE}
                  dataKey="value"
                >
                  {insightsData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  content={RenderLegend}
                  layout="vertical"
                  verticalAlign="middle"
                  wrapperStyle={legendWrapperStyle}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className={styles.totalInfo}>
            <p>
              <b>
                {insightsData[0] &&
                  insightsData[0].value + insightsData[1].value}
              </b>{' '}
              total students
            </p>
          </div>
        </Fragment>
      )}
      {SHOW_ONLY_LEGEND && (
        <RenderLegend
          payload={
            insightsData &&
            insightsData.map((d) => ({
              value: d.name,
              payload: { value: d.value },
            }))
          }
        />
      )}
    </div>
  );
}

QQLoginPieChart.propTypes = {
  data: PropTypes.object,
  containerWidth: PropTypes.number,
};

function mapStateToProps(state) {
  const containerWidth = get(state, 'insights.containerWidth', 0);

  return {
    containerWidth,
  };
}

export default connect(mapStateToProps)(QQLoginPieChart);
